import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { AlertCircle, Loader2 } from "lucide-react"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface InfluencerDialogProps {
  open: boolean
  mode: "create" | "add-code"
  onOpenChange: (open: boolean) => void
  onSubmit: () => void
  errors: {
    general?: string
    code?: string
    name?: string
  }
  codeValue: string
  onCodeChange: (value: string) => void
  nameValue: string
  onNameChange: (value: string) => void
  isSubmitting: boolean
  targetInfluencerName?: string
}

export const InfluencerDialog = ({
  open,
  mode,
  onOpenChange,
  onSubmit,
  errors,
  codeValue,
  onCodeChange,
  nameValue,
  onNameChange,
  isSubmitting,
  targetInfluencerName,
}: InfluencerDialogProps) => {
  const isCreateMode = mode === "create"

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isCreateMode ? "Add Influencer" : "Add Discount Code"}</DialogTitle>
          <DialogDescription>
            {isCreateMode
              ? "Register a new influencer with an initial discount code."
              : `Register an additional discount code for ${targetInfluencerName}.`}
          </DialogDescription>
        </DialogHeader>
        {errors.general && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errors.general}</AlertDescription>
          </Alert>
        )}
        <div className="grid gap-4 py-4">
          {isCreateMode ? (
            <div className="grid gap-2">
              <Label htmlFor="influencer-name">
                <span>Influencer Name</span> <span className="text-destructive">*</span>
              </Label>
              <Input
                id="influencer-name"
                placeholder="Enter influencer name"
                value={nameValue}
                onChange={(event) => onNameChange(event.target.value)}
                disabled={isSubmitting}
                className={cn(errors.name && "border-destructive")}
              />
              {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
            </div>
          ) : (
            <div className="grid gap-1">
              <Label className="text-sm text-muted-foreground">Influencer</Label>
              <p className="text-base font-medium">{targetInfluencerName}</p>
            </div>
          )}
          <div className="grid gap-2">
            <Label htmlFor="code">
              <span>Discount Code</span> <span className="text-destructive">*</span>
            </Label>
            <Input
              id="code"
              placeholder="Enter discount code"
              value={codeValue}
              onChange={(event) => onCodeChange(event.target.value)}
              disabled={isSubmitting}
              className={cn(errors.code && "border-destructive")}
            />
            {errors.code && <p className="text-sm text-destructive">{errors.code}</p>}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : isCreateMode ? (
              "Add Influencer"
            ) : (
              "Add Code"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
